import * as config from './config';
import { format } from 'date-fns';

// Parse a date-only value ('YYYY-MM-DD') as LOCAL midnight so a calendar shows,
// and formatting renders, the same day the user picked — in any timezone.
// `new Date('2026-07-28')` parses as UTC and renders as the previous day in US
// timezones, which is the classic off-by-one delivery-date bug. Full datetime
// strings (with a time component) and Date objects are passed through as-is.
export const parseDateOnly = (value?: string | Date | null): Date | null => {
  if (value === undefined || value === null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

// Serialize a Date to 'YYYY-MM-DD' using LOCAL components (not UTC), so the
// stored day matches what the user selected in every timezone.
export const formatDateOnly = (date?: Date | null): string => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const formatDate = (date: string | Date): string => {
  return format(parseDateOnly(date) ?? new Date(date), config.DATE_FORMAT);
};

export const formatDateTime = (date: string | Date): string => {
  return format(new Date(date), config.DATETIME_FORMAT);
};

export const isValidFileType = (file: File): boolean => {
  return config.ALLOWED_FILE_TYPES.includes(file.type);
};

export const isValidFileSize = (file: File): boolean => {
  return file.size <= config.MAX_FILE_SIZE;
};

export const getPageCount = (totalItems: number, pageSize: number): number => {
  return Math.ceil(totalItems / pageSize);
};

export const getPaginationRange = (
  currentPage: number,
  totalPages: number,
  maxPages: number = 5
): number[] => {
  if (totalPages <= maxPages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const halfMax = Math.floor(maxPages / 2);
  let start = currentPage - halfMax;
  let end = currentPage + halfMax;

  if (start < 1) {
    end += Math.abs(start) + 1;
    start = 1;
  }

  if (end > totalPages) {
    start -= end - totalPages;
    end = totalPages;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): Promise<ReturnType<F>> =>
    new Promise((resolve) => {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => resolve(func(...args)), waitFor);
    });
};

export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

export const generateUniqueId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

// Export the configuration object
export const appConfig = config;